/* eslint-disable @typescript-eslint/naming-convention */
import { injectable } from '@servicetitan/react-ioc';
import { action, computed, makeObservable, observable, reaction, runInAction, when } from 'mobx';

import { ChatStore, IMessage } from '../../common/stores/chat.store';
import { QuestionGroupApiModel, QuestionsApi } from '../../common/api/questions.api';
import { IQuestionOptionModel } from '../models/questions.model';
import { ChatApi, EquipmentQuestionRequestModel } from '../../common/api/chat.api';
import { MessageType } from '../../common/models/message-type.model';
import { FeedbackApi, FeedbackRequestModel } from '../../common/api/feedback.api';
import { ErrorModel } from '../../common/models/error.model';
import axios, { CancelTokenSource } from 'axios';
import { getEquipmentLabel } from '../../equipment-picker/components/search-equipment';
import { getBaseRequestParams } from '../../common/utils/base-request-params';
import {
    DocumentIfNamePair,
    EquipmentDocumentApiModel,
    EquipmentsApi,
    MappedEquipmentApiModel,
} from '../../common/api/equipments.api';
import { BattlecardStore } from '../../common/stores/battlecard.store';
import { MainStore } from '../../common/stores/main.store';
import { TopicType } from '../../common/api/topics.api';

const wait = (mills: number) => {
    return new Promise(resolve => setTimeout(resolve, mills));
};

const DOWNLOAD_PDFS_ID = 'download_pdfs';
const ERROR_CODES_ID = 'error_codes';

export interface EquipmentAnswerCiteViewModel {
    answer: string;
    citations: {
        text: string;
        file?: EquipmentDocumentApiModel;
        pageNumber: number;
    }[];
}

const predefinedQuestions: IQuestionOptionModel[] = [
    { id: '-1', text: 'What is the model number of this equipment?', isGroup: false },
    { id: '-2', text: 'Show me the last 3 sold invoices', isGroup: false },
    { id: '-3', text: 'Does this customer have any open estimates?', isGroup: false },
];

@injectable()
export class QuestionsStore {
    @observable
    loading = false;

    @observable
    selectedTopicGroups: QuestionGroupApiModel[] = [];

    @observable
    selectedQuestion?: IQuestionOptionModel;

    @observable
    selectedQuestionGroup?: IQuestionOptionModel;

    lastQuestion?: {
        id?: string;
        content: string;
    };
    lastAnswer?: any;

    getQuestionCancelToken?: CancelTokenSource = undefined;

    documentWithUrlList: EquipmentDocumentApiModel[] = [];

    @observable
    showBattlecardLoading = false;

    // TODO: temporary do not show info page
    showLinkToReadyMePage = false;

    constructor(
        private readonly chatStore: ChatStore,
        private readonly mainStore: MainStore,
        private readonly battlecardStore: BattlecardStore,
        private readonly questionsApi: QuestionsApi,
        private readonly chatApi: ChatApi,
        private readonly equipmentApi: EquipmentsApi,
        private readonly feedbackApi: FeedbackApi
    ) {
        makeObservable(this);

        this.init();

        reaction(
            () => {
                return this.battlecardStore.battlecardError;
            },
            () => {
                if (this.battlecardStore.battlecardError && this.mainStore.selectedAppointment) {
                    this.chatStore.addMessage({
                        isMine: false,
                        data: {
                            title: 'Something went wrong',
                            message: `Unable to retrieve details for the selected job: ${this.mainStore.selectedAppointment.name}. Please try launching the field assistant later`,
                        } as ErrorModel,
                        type: MessageType.Error,
                    });
                    return { hasError: true };
                }
            },
            { fireImmediately: true }
        );
    }

    @computed
    get groupsQuestionsMapping(): Map<QuestionGroupApiModel['id'], IQuestionOptionModel[]> {
        return new Map<QuestionGroupApiModel['id'], IQuestionOptionModel[]>(
            this.selectedTopicGroups.map(group => {
                const questions = group.questions.map<IQuestionOptionModel>(q => ({
                    id: q.id,
                    text: q.text,
                    isGroup: false,
                }));
                return [group.id, questions];
            })
        );
    }

    @computed
    get isEquipmentSelected() {
        return this.mainStore.selectedEquipmentInfo !== undefined;
    }

    @computed
    get isStInfoTopicSelected() {
        return this.mainStore.selectedTopic?.type === TopicType.InformationInServiceTitan;
    }

    @computed
    get isEquipmentOrTroubleshootingSelected() {
        return this.mainStore.selectedTopic?.type !== TopicType.InformationInServiceTitan;
    }

    init = () => {
        this.chatStore.resetMessaging();

        if (this.isEquipmentOrTroubleshootingSelected && this.mainStore.selectedEquipmentInfo) {
            let text: string;
            if (this.mainStore.selectedTopic?.type === TopicType.EquipmentInformation) {
                text = `What questions can I answer about <strong>${getEquipmentLabel(this.mainStore.selectedEquipmentInfo)}</strong> related to <strong>Equipment Information</strong>?`;
            } else {
                text = `What questions can I answer about <strong>${getEquipmentLabel(this.mainStore.selectedEquipmentInfo)}</strong> related to <strong>Troubleshooting</strong>?`;
            }
            if (!this.mainStore.equipmentHasDocumentation) {
                text = `Just a reminder, there’s no documentation for this <strong>${getEquipmentLabel(this.mainStore.selectedEquipmentInfo)}</strong> equipment yet, so responses may not be accurate.
                        Feel free to ask, and I’ll do my best to help!`;
            }

            this.chatStore.addMessage({
                text,
                isMine: false,
                type: MessageType.RichText,
                showAvatar: true,
            });
        }

        if (this.isStInfoTopicSelected) {
            this.chatStore.enqueueMessages([
                {
                    isMine: false,
                    type: MessageType.RichText,
                    text: 'How can I help you today? Here are some example questions you can ask:',
                    showAvatar: true,
                },
                {
                    isMine: false,
                    type: MessageType.Question,
                    data: predefinedQuestions,
                    showAvatar: false,
                },
            ]);
        }
    };

    @action
    proceedFrom = async () => {
        await this.mainStore.waitForInit;
        await this.loadQuestions();
    };

    dispose = () => {
        this.getQuestionCancelToken?.cancel();
    };

    @action
    loadQuestions = async () => {
        this.loading = true;
        this.getQuestionCancelToken?.cancel();
        this.getQuestionCancelToken = axios.CancelToken.source();

        try {
            const { data } = await this.questionsApi.getQuestions(
                {
                    ...getBaseRequestParams(),
                },
                this.getQuestionCancelToken.token
            );
            this.getQuestionCancelToken = undefined;

            const topic = data.topics.find(c => c.id === this.mainStore.selectedTopic?.id);
            if (!topic) {
                return;
            }

            runInAction(() => {
                this.selectedTopicGroups = topic.groups ?? [];
            });

            const questions = [];
            if (this.isEquipmentOrTroubleshootingSelected) {
                questions.push(
                    ...this.selectedTopicGroups.map<IQuestionOptionModel>(g => ({
                        id: g.id,
                        isGroup: true,
                        text: g.name,
                    }))
                );
            }

            if (
                this.isEquipmentOrTroubleshootingSelected &&
                this.mainStore.equipmentHasDocumentation
            ) {
                questions.unshift({
                    id: ERROR_CODES_ID,
                    isGroup: false,
                    text: `Error Codes`,
                });

                questions.push({
                    id: DOWNLOAD_PDFS_ID,
                    isGroup: false,
                    text: `View PDF documents`,
                });
            }

            if (this.mainStore.equipmentHasDocumentation) {
                this.chatStore.enqueueMessages([
                    {
                        isMine: false,
                        type: MessageType.Question,
                        data: questions,
                        date: new Date(),
                        showAvatar: false,
                    },
                ]);
            }
            if (this.isStInfoTopicSelected) {
                const messages: IMessage[] = [
                    {
                        isMine: false,
                        type: MessageType.Button,
                        text: 'See more example questions',
                        showAvatar: false,
                        onClick: this.handleShowTopicQuestions,
                    },
                ];
                // TODO: temporary do not show info page
                if (this.showLinkToReadyMePage) {
                    messages.push({
                        isMine: false,
                        type: MessageType.Button,
                        text: 'What can FieldAssist do?',
                        showAvatar: false,
                        onClick: this.handleOpenInfoPage,
                        date: new Date(),
                    });
                }
                this.chatStore.enqueueMessages(messages);
            }
        } catch (error: any) {
            if (axios.isCancel(error)) {
                return;
            }
            if (axios.isAxiosError(error)) {
                return;
            }
            throw error;
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    };

    @action
    handleShowTopicQuestions = () => {
        const questions = [];
        questions.push(
            ...(this.selectedTopicGroups ?? []).map<IQuestionOptionModel>(g => ({
                id: g.id,
                isGroup: true,
                text: g.name,
            }))
        );
        if (this.mainStore.selectedTopic?.id !== 2 || this.mainStore.equipmentHasDocumentation) {
            this.chatStore.enqueueMessages([
                {
                    isMine: false,
                    type: MessageType.Question,
                    data: questions,
                    date: new Date(),
                    showAvatar: false,
                },
            ]);
        }
    };

    handleOpenInfoPage = () => {
        window.location.href = '/#/readme';
    };

    handleQuestionSelect = (question: IQuestionOptionModel) => {
        if (question.isGroup) {
            this.handleQuestionGroupSelect(question);
        } else {
            if (this.isEquipmentSelected) {
                if (question.id === DOWNLOAD_PDFS_ID) {
                    this.showDownloadDocuments();
                } else if (question.id === ERROR_CODES_ID) {
                    this.showErrorCodes();
                } else {
                    this.handleMessage(question.text, question.id);
                }
            } else {
                this.handleExactQuestionsSelect(question);
            }
        }
    };

    @action
    handleQuestionGroupSelect = async (question: IQuestionOptionModel) => {
        const groupQuestions = this.groupsQuestionsMapping.get(question.id) ?? [];
        this.setSelectedQuestionGroup(question);

        this.chatStore.enqueueMessages([
            {
                isMine: true,
                type: MessageType.Text,
                text: question.text,
                showAvatar: false,
                date: new Date(),
            },
        ]);

        this.loading = true;
        await wait(1000);
        runInAction(() => {
            this.loading = false;
        });
        this.chatStore.enqueueMessages([
            {
                isMine: false,
                type: MessageType.RichText,
                text: `What questions can I answer related to <strong>${question.text}</strong>?`,
                showAvatar: true,
            },
            {
                isMine: false,
                type: MessageType.Question,
                data: groupQuestions,
                date: new Date(),
                showAvatar: false,
            },
        ]);
    };

    @action
    handleExactQuestionsSelect = async (question: IQuestionOptionModel) => {
        if ((await this.waitForBattlecard()).hasError) {
            return;
        }
        this.lastQuestion = {
            id: question.id,
            content: question.text,
        };
        this.chatStore.enqueueMessages([
            {
                isMine: true,
                type: MessageType.Text,
                text: question.text,
                date: new Date(),
                showAvatar: false,
            },
        ]);
        this.setSelectedQuestion(question);

        try {
            this.loading = true;
            const { data } = await this.chatApi.sendPredefinedQuestion({
                ...getBaseRequestParams(),
                job_id: this.mainStore.selectedAppointment!.id,
                question: {
                    content: question.text,
                    id: question.id,
                    role: 'user',
                    timestamp: new Date(),
                },
            });
            this.lastAnswer = data.response.content;
            const { content, entity_type } = data.response;
            const messages: IMessage[] = [];
            if (content.intro) {
                messages.push({
                    isMine: false,
                    showAvatar: true,
                    data: content.intro,
                    type: MessageType.Markdown,
                });
            }

            messages.push(
                {
                    isMine: false,
                    data: content.entity_data,
                    type: entity_type,
                    showAvatar: !content.intro,
                },
                {
                    isMine: false,
                    type: MessageType.Feedback,
                    date: new Date(),
                    showAvatar: false,
                }
            );

            this.chatStore.enqueueMessages(messages);
        } catch (err) {
            if (axios.isAxiosError(err)) {
                return;
            }
            this.chatStore.enqueueMessages([
                {
                    isMine: false,
                    showAvatar: true,
                    text: `Sorry, I couldn't answer your question. Please try rephrasing or asking a different question, and I'll do my best to help.`,
                    type: MessageType.Text,
                },
            ]);
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    };

    @action
    handleMessage = async (message: string, id?: string) => {
        if ((await this.waitForBattlecard()).hasError) {
            return;
        }
        if (!message.length) {
            return Promise.resolve();
        }

        this.chatStore.enqueueMessages([
            {
                isMine: true,
                text: message,
                type: MessageType.Text,
                date: new Date(),
                showAvatar: false,
            },
        ]);
        this.lastQuestion = {
            id,
            content: message,
        };

        if (this.isEquipmentSelected) {
            await this.handleEquipmentQuestion(message);
        } else {
            await this.handleFreeFormQuestion(message);
        }
    };

    @action
    handleFreeFormQuestion = async (message: string) => {
        this.loading = true;
        try {
            const { data } = await this.chatApi.sendFreeFormQuestion({
                id: window.crypto.randomUUID(),
                job_id: this.mainStore.selectedAppointment?.id ?? 0,
                message: {
                    content: message,
                    role: 'user',
                    timestamp: new Date(),
                },
                ...getBaseRequestParams(),
            });
            const { content, entity_type } = data.response;

            this.lastAnswer = content;

            this.chatStore.enqueueMessages([
                {
                    showAvatar: true,
                    isMine: false,
                    data: content.entity_data,
                    type: entity_type,
                },
                {
                    showAvatar: false,
                    isMine: false,
                    type: MessageType.Feedback,
                    date: new Date(),
                },
            ]);
        } catch (err) {
            if (axios.isAxiosError(err)) {
                return;
            }
            this.chatStore.enqueueMessages([
                {
                    isMine: false,
                    showAvatar: true,
                    text: `Sorry, I couldn't answer your question. Please try rephrasing or asking a different question, and I'll do my best to help.`,
                    type: MessageType.Text,
                },
            ]);
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    };

    @computed
    get documents(): EquipmentDocumentApiModel[] {
        const docs = (this.mainStore.selectedMappedEquipments ?? []).flatMap(x => x.documents);
        return [...new Map(docs.map(x => [x.id, x])).values()];
    }

    @computed
    get manufacturerByDocumentId(): Map<string, MappedEquipmentApiModel> {
        const map = new Map<string, MappedEquipmentApiModel>();
        (this.mainStore.selectedMappedEquipments ?? []).forEach(equipment => {
            equipment.documents.forEach(doc => {
                map.set(doc.id, equipment);
            });
        });
        return map;
    }

    @computed
    get documentNamesByIdMap(): Map<string, string> {
        const set = new Map<string, string>();
        const map = new Map<string, string>();
        this.documents.forEach(doc => {
            let docName = this.getFileNameByDocumentId(doc.id);
            if (set.has(docName)) {
                const existingDocId = set.get(docName)!;
                map.set(existingDocId, `${docName}_1`);
                let i = 2;
                while (true) {
                    const tempName = `${docName}_${i}`;
                    if (!set.has(tempName)) {
                        docName = tempName; // Ensure unique names
                        break;
                    }
                    i++;
                }
            }
            set.set(docName, doc.id);
            map.set(doc.id, docName);
        });

        return map;
    }

    @action
    handleEquipmentQuestion = async (message: string) => {
        this.loading = true;
        try {
            const requestParams: EquipmentQuestionRequestModel = {
                id: window.crypto.randomUUID(),
                type: 'chat',
                question: message,
                mapped_equipments: this.mainStore.selectedMappedEquipments ?? [],
                monolith_model_number: this.mainStore.selectedMonolithEquipment?.model,
                monolith_equipment: this.mainStore.selectedMonolithEquipment
                    ? {
                          ...this.mainStore.selectedMonolithEquipment,
                      }
                    : undefined,
                job_id: this.mainStore.selectedAppointment!.id,
                ...getBaseRequestParams(),
            };

            let response;
            if (this.mainStore.selectedTopic?.type === TopicType.EquipmentInformation) {
                response = await this.chatApi.sendEquipmentQuestion(requestParams);
            } else {
                response = await this.chatApi.sendTroubleshootingQuestion(requestParams);
            }

            const { data } = response;

            const { answer } = data;
            this.lastAnswer = answer;

            const documentIds = data.answer.citations.map(x => x.document_id);
            const documentIdsWithoutUrl = documentIds.filter(
                x => !this.documentWithUrlList.map(y => y.id).includes(x)
            );

            if (documentIdsWithoutUrl.length > 0) {
                // load urls
                const { data } = await this.equipmentApi.loadPdfUrls({
                    ...getBaseRequestParams(),
                    location_id: this.mainStore.selectedAppointment?.locationId,
                    documents: documentIdsWithoutUrl.map(x => ({
                        id: x,
                        download_file_name: this.documentNamesByIdMap.get(x)!,
                    })),
                });

                this.documentWithUrlList.push(
                    ...data.document_files.map(x => ({
                        ...x,
                        download_file_name: this.documentNamesByIdMap.get(x.id),
                    }))
                );
            }

            const messageData: EquipmentAnswerCiteViewModel = {
                answer: answer.answer_text,
                citations: answer.citations
                    .map(c => {
                        const file = this.documentWithUrlList.find(x => x.id === c.document_id);
                        return {
                            file,
                            pageNumber: c.page_number,
                            text: `This response references page ${c.page_number} of <b>${file?.download_file_name}</b>`,
                        };
                    })
                    .filter(x => x.file),
            };

            const messages: any[] = [
                {
                    isMine: false,
                    showAvatar: true,
                    date: new Date(),
                    type: MessageType.EquipmentAnswerCite,
                    data: messageData,
                },
                {
                    showAvatar: false,
                    isMine: false,
                    type: MessageType.Feedback,
                    date: new Date(),
                },
            ];

            // load file urls
            if (answer.has_found_answer === false) {
                messages.push({
                    isMine: false,
                    type: MessageType.Question,
                    data: [
                        {
                            id: DOWNLOAD_PDFS_ID,
                            isGroup: false,
                            text: `View PDF documents`,
                        },
                    ],
                    date: new Date(),
                    showAvatar: false,
                });
            }
            this.chatStore.enqueueMessages(messages);
        } catch (err) {
            if (axios.isAxiosError(err)) {
                return;
            }
            this.chatStore.enqueueMessages([
                {
                    isMine: false,
                    showAvatar: true,
                    text: `Sorry, I couldn't answer your question. Please try rephrasing or asking a different question, and I'll do my best to help.`,
                    type: MessageType.Text,
                },
            ]);
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    };

    @action
    handleFeedbackYes = async () => {
        try {
            this.loading = true;

            await this.feedbackApi.sendFeedbackNew(this.getFeedbackParams(true));
            this.chatStore.enqueueMessages([
                {
                    isMine: false,
                    showAvatar: true,
                    text: `Great! I'm glad I could help. Let me know if you need anything else.`,
                    type: MessageType.Text,
                },
            ]);
        } catch {
            this.chatStore.enqueueMessages([
                {
                    isMine: false,
                    showAvatar: true,
                    type: MessageType.Error,
                    data: {
                        title: 'Something went wrong',
                        message: `Unable to submit your feedback at the moment. Please try again later.`,
                    } as ErrorModel,
                },
            ]);
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    };

    @action
    handleFeedbackNo = async (feedback: string) => {
        if (feedback.length) {
            this.chatStore.enqueueMessages([
                {
                    isMine: true,
                    type: MessageType.Text,
                    date: new Date(),
                    text: feedback,
                },
            ]);
        }
        try {
            this.loading = true;

            await this.feedbackApi.sendFeedbackNew(this.getFeedbackParams(false));
            if (feedback.length) {
                this.chatStore.enqueueMessages([
                    {
                        isMine: false,
                        showAvatar: true,
                        text: `Thank you for your feedback! We apologize for the issue and will make sure to learn from it and improve going forward.`,
                        type: MessageType.Text,
                    },
                ]);
            }
        } catch {
            this.chatStore.enqueueMessages([
                {
                    isMine: false,
                    showAvatar: true,
                    type: MessageType.Error,
                    data: {
                        title: 'Something went wrong',
                        message: `Unable to submit your feedback at the moment. Please try again later.`,
                    } as ErrorModel,
                },
            ]);
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    };

    @action
    showDownloadDocuments = async () => {
        this.chatStore.enqueueMessages([
            {
                isMine: true,
                type: MessageType.Text,
                text: 'View PDF documents',
                date: new Date(),
                showAvatar: false,
            },
        ]);

        const documentIds = (
            this.mainStore.selectedMappedEquipments ?? []
        ).flatMap<DocumentIfNamePair>(doc => {
            return doc.documents.map(d => ({
                id: d.id,
                download_file_name: this.documentNamesByIdMap.get(d.id)!,
            }));
        });

        if (documentIds.length === 0) {
            return;
        }
        this.loading = true;
        let documentsWithUrls: EquipmentDocumentApiModel[] = [];
        try {
            const response = await this.equipmentApi.loadPdfUrls({
                ...getBaseRequestParams(),
                documents: documentIds,
                location_id: this.mainStore.selectedAppointment?.locationId,
            });
            documentsWithUrls = response.data.document_files.map(x => ({
                ...x,
                download_file_name: this.documentNamesByIdMap.get(x.id),
            }));
            // update already fetched urls
            this.documentWithUrlList.push(...documentsWithUrls);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                return;
            }
            throw error;
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }

        this.chatStore.enqueueMessages([
            {
                isMine: false,
                type: MessageType.Text,
                text: 'Which PDFs would you like to view?',
                showAvatar: true,
            },
            {
                isMine: false,
                type: MessageType.DownloadDocuments,
                date: new Date(),
                data: [...documentsWithUrls].sort((a, b) =>
                    (a.download_file_name ?? '').localeCompare(b.download_file_name ?? '')
                ),
                showAvatar: false,
            },
        ]);
    };

    @action
    showErrorCodes = () => {
        this.chatStore.enqueueMessages([
            {
                isMine: true,
                type: MessageType.Text,
                text: 'Error Codes',
                date: new Date(),
                showAvatar: false,
            },
            {
                isMine: false,
                type: MessageType.RichText,
                text:
                    'Please type your question in one of the following formats: ' +
                    '<ul>' +
                    '<li><strong>"Error Code 515"</strong></li>' +
                    '<li><strong>"5 fast flashes and 3 slow flashes"</strong></li>' +
                    '<li><strong>"Green flashed 2 times red flashed 1 time"</strong></li>' +
                    '</ul>',
                showAvatar: true,
            },
        ]);
    };

    @action
    setSelectedQuestion(question?: typeof this.selectedQuestion) {
        this.selectedQuestion = question;
    }

    @action
    setSelectedQuestionGroup(questionGroup?: typeof this.selectedQuestionGroup) {
        this.selectedQuestionGroup = questionGroup;
    }

    @action
    waitForBattlecard = async () => {
        // should be in ST info topic
        if (!this.isStInfoTopicSelected) {
            return { hasError: false };
        }
        if (this.battlecardStore.battlecardLoading) {
            this.showBattlecardLoading = true;
            await when(() => !this.battlecardStore.battlecardLoading);
            runInAction(() => {
                this.showBattlecardLoading = false;
            });
            if (this.battlecardStore.battlecardError && this.mainStore.selectedAppointment) {
                return { hasError: true };
            }
        }
        return { hasError: false };
    };

    private getFeedbackParams = (is_helpful: boolean): FeedbackRequestModel => {
        const endpointType =
            this.mainStore.selectedTopic?.type === TopicType.InformationInServiceTitan
                ? 'chat'
                : this.mainStore.selectedTopic?.type === TopicType.EquipmentInformation
                  ? 'equipment_chat'
                  : 'troubleshooting_chat';
        return {
            endpoint_type: endpointType,
            message: {
                is_helpful,
                question: this.lastQuestion!,
                answer: JSON.stringify(this.lastAnswer),
                details: '',
            },

            job_id: this.mainStore.selectedAppointment!.id,

            ...getBaseRequestParams(),
        };
    };

    private getFileNameByDocumentId = (documentId: string): string => {
        const manufacturer = this.manufacturerByDocumentId.get(documentId);
        const document = this.documents.find(doc => doc.id === documentId);
        if (manufacturer) {
            return `${manufacturer.name}-${document?.doc_type ?? ''}`;
        }
        return '';
    };
}
